<template>
    <div v-if="isActive" class="inline-prescription-entry">
        <b-card no-body class="shadow-lg">
            <b-card-header class="bg-primary text-white d-flex justify-content-between align-items-center">
                <span><i class="fa-solid fa-pills"></i> Quick Prescription Entry</span>
                <b-button
                    variant="outline-light"
                    size="sm"
                    @click="onCancel">
                    <i class="fa-solid fa-xmark"></i>
                    Cancel
                </b-button>
            </b-card-header>
            <b-card-body>
                <quick-entry-input
                    ref="quickEntryInput"
                    v-model="quickEntry"
                    :show-label="false"
                    @parsed="onQuickEntryParsed"
                    @error="onQuickEntryError"
                />

                <div v-if="parsedData" class="mt-3">
                    <b-alert show variant="info">
                        <div>
                            <strong>Prescription:</strong> {{ parsedDescription }}
                        </div>
                        <div v-if="parsedPeriod" class="mt-1">
                            <strong>Period:</strong> {{ parsedPeriod }}
                        </div>
                        <div v-if="parsedData.repeat_info" class="mt-1">
                            <strong>Repeat Info:</strong> {{ parsedData.repeat_info }}
                        </div>
                    </b-alert>

                    <div class="text-center">
                        <b-button 
                            variant="success" 
                            class="mr-2"
                            :disabled="isSubmitting"
                            @click="addPrescription">
                            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
                            <i v-else class="fa-solid fa-plus"></i>
                            Add Prescription
                        </b-button>
                    </div>
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import QuickEntryInput from './QuickEntryInput'

export default {
    name: 'inline-prescription-entry',
    components: {
        QuickEntryInput
    },
    data() {
        return {
            isActive: false,
            quickEntry: '',
            parsedData: null,
            isSubmitting: false,
            form: new Form({
                medication_id: null,
                is_chronic: false,
                other: "",
                strength: null,
                unit_id: null,
                frequency_usage: null,
                frequency_unit_id: null,
                dosage_amount: null,
                dosage_form_id: null,
                amount_of_time: null,
                delivery_form_id: null,
                instructions: "",
                stoppage_reason: "",
                repeat_prescription: 0,
                repeat_date_id: null,
                administrative_datetime: null,
                consistency_token: null
            })
        }
    },
    computed: {
        parsedDescription() {
            return this.parsedData?.full_prescription || this.parsedData?.description || ''
        },
        parsedPeriod() {
            const amount = this.parsedData?.amount_of_time
            const totalAmount = this.parsedData?.total_amount
            const frequencyUnit = this.parsedData?.frequency_unit_name || 'day'
            const dosageForm = this.parsedData?.dosage_form_name || 'tablet'
            if (
                amount !== null && amount !== undefined && amount !== '' &&
                totalAmount !== null && totalAmount !== undefined && totalAmount !== ''
            ) {
                return `${amount} ${frequencyUnit}(s) - ${totalAmount} ${dosageForm}(s)`
            }
            return null
        }
    },
    methods: {
        ...mapActions('prescriptions', ['createPatientPrescription']),
        resetPrescriptionForm() {
            this.form.reset()
        },
        applyParsedDataToForm(data) {
            if (!this.form) {
                this.resetPrescriptionForm()
            }

            const source = data?.form || data || {}
            this.form.populate({
                medication_id: source.medication_id ?? null,
                is_chronic: Boolean(source.is_chronic),
                other: source.other || '',
                strength: source.strength ?? null,
                unit_id: source.unit_id ?? null,
                frequency_usage: source.frequency_usage ?? null,
                frequency_unit_id: source.frequency_unit_id ?? null,
                dosage_amount: source.dosage_amount ?? null,
                dosage_form_id: source.dosage_form_id ?? null,
                amount_of_time: source.amount_of_time ?? null,
                delivery_form_id: source.delivery_form_id ?? null,
                instructions: source.instructions || '',
                repeat_prescription: source.repeat_prescription ?? 0,
                repeat_date_id: source.repeat_date_id ?? null,
            })
        },
        activate(initialText = '') {
            this.isActive = true
            this.quickEntry = initialText
            this.resetPrescriptionForm()
            this.$nextTick(() => {
                this.$refs.quickEntryInput?.$el?.querySelector('input')?.focus()
            })
        },

        onCancel() {
            this.isActive = false
            this.quickEntry = ''
            this.parsedData = null
            this.resetPrescriptionForm()
            this.$refs.quickEntryInput?.clearEntry()
            this.$emit('cancel')
        },

        onQuickEntryParsed(parsed) {
            this.parsedData = parsed
            this.applyParsedDataToForm(parsed)
            this.$emit('parsed', parsed)
        },

        onQuickEntryError(error) {
            this.parsedData = null
            this.$emit('error', error)
        },

        async addPrescription() {
            if (!this.parsedData || this.isSubmitting) {
                return
            }

            try {
                this.isSubmitting = true
                const prescriptionData = await this.createPatientPrescription(this.form.data())
                
                this.$emit('prescription-added', {
                    data: prescriptionData,
                    parsed: this.parsedData,
                    originalText: this.quickEntry
                })

                this.$bvToast.toast(
                    'Prescription added successfully',
                    {
                        title: 'Success',
                        variant: 'success',
                        solid: true
                    }
                )

                this.onCancel()
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Failed to add prescription'
                this.$bvToast.toast(errorMsg, {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                })
            } finally {
                this.isSubmitting = false
            }
        }
    }
}
</script>

<style scoped>
.inline-prescription-entry {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1050;
    width: 90%;
    max-width: 600px;
}

.inline-prescription-entry .card {
    border: 2px solid #007bff;
}

@media (min-width: 992px) {
    .inline-prescription-entry {
        position: static;
        top: auto;
        left: auto;
        transform: none;
        width: 100%;
        max-width: none;
        margin-top: 1rem;
    }
}
</style>
